import Head from 'next/head'
import Link from 'next/link'
import { useEffect } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import LoadingIcon from '@/components/icons/Loading'
import Layout from '@/components/Layout'
import { useCart } from '@/components/providers/CartProvider'
import useMoneyFormatter from '@/hooks/useMoneyFormatter'
import useQuantity from '@/hooks/useQuantity'

declare const process: {
	env: {
		NEXT_PUBLIC_BACKEND_API_URL: string | undefined,
	},
}

interface Item {
	id: string,
	parent_id?: null,
	variant_id: number,
	product_id: number,
	sku: string,
	name: string,
	url: string,
	quantity: number,
	taxable: boolean,
	image_url: string | null,
	discounts?: Array<unknown> | null,
	coupons?: Array<unknown> | null,
	discount_amount: number,
	coupon_amount: number,
	list_price: number,
	sale_price: number,
	extended_list_price: number,
	extended_sale_price: number,
	is_require_shipping: boolean,
	is_mutable: boolean,
}

function LineItem({ item }: { item: Item }): JSX.Element {
	const { updateQuantity, removeFromCart } = useCart()
	const { quantity, handleQuantityInput, handleQuantityPlus, handleQuantityMinus, handleQuantityBlur } = useQuantity(item.quantity)
	const money = useMoneyFormatter()
	const debouncedUpdateQuantity = useDebouncedCallback(updateQuantity, 500)

	useEffect(() => {
		if (isNaN(quantity)) {
			return
		}

		void debouncedUpdateQuantity(item.id, { id: item.product_id }, quantity)
	}, [quantity])

	const link = `/p/${encodeURIComponent(item.sku)}`

	return (
		<div className="bg-white shadow rounded-lg p-3 mt-2 sm:mt-3 sm:py-4 sm:pl-6 sm:pr-3 xl:mt-4 2xl:mt-5">
			<div className="flex justify-between items-center relative">

				<div className="flex items-center">
					<Link href={link}>
						<img className="w-16 h-16 sm:w-28 sm:h-28" src={item.image_url ?? '/img/Image-Coming-Soon.jpg'} alt={item.image_url === null ? 'image coming soon' : item.name }/>
					</Link>

					<div className="ml-4 pt-4 sm:pt-0 sm:ml-8">
						<div>
							<Link href={link}>
								<a>
									<h4 className="text-sm font-bold sm:text-base xl:text-lg">{item.name}</h4>
								</a>
							</Link>
							<Link href={link}>
								<a>
									<p className="text-xs font-light sm:text-sm xl:text-base">SKU: {item.sku}</p>
								</a>
							</Link>
						</div>

						<div className="hidden bg-gray-200 border px-1 rounded-full justify-between items-center mt-6 sm:inline-flex md:px-2">
							<button onClick={handleQuantityMinus} className="focus:outline-none rounded-l-full">
								<svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 md:w-4 md:h-4" viewBox="0 0 20 20" fill="currentColor">
									<path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
								</svg>
							</button>

							<input value={quantity} onBlur={handleQuantityBlur} onChange={handleQuantityInput} className={`mx-1 py-px w-8 bg-white text-xs text-center sm:text-sm md:w-10 md:mx-1.5 md:text-base ${quantity.toString().length > 3 ? 'w-14 md:w-16' : ''}`} type="number"></input>

							<button onClick={handleQuantityPlus} className="focus:outline-none rounded-r-full">
								<svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 md:w-4 md:h-4" viewBox="0 0 20 20" fill="currentColor">
									<path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
								</svg>
							</button>
						</div>
					</div>
				</div>

				<div className="hidden sm:block sm:text-right sm:mr-8">
					<p className="text-lg font-extrabold sm:text-xl md:text-2xl xl:text-3xl">{money.format(item.extended_sale_price)}</p>
					<p className="text-xs font-light md:text-sm xl:text-base">{money.format(item.sale_price)} each</p>
				</div>

				<button onClick={() => void removeFromCart(item.id)} className="absolute top-0 right-0 h-4 w-4 sm:h-6 sm:w-6">
					<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>
			</div>

			<div className="flex justify-between items-center mt-4 sm:hidden">

				<div className="bg-gray-200 border px-1 rounded-full justify-between items-center inline-flex sm:hidden">
					<button onClick={handleQuantityMinus} className="focus:outline-none rounded-l-full relative">
						<svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
							<path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
						</svg>
					</button>

					<input value={quantity} onBlur={handleQuantityBlur} onChange={handleQuantityInput} className={`mx-1 py-0.5 bg-white text-xs w-8 text-center ${quantity.toString().length > 3 ? 'w-12' : ''}`} type="number"></input>

					<button onClick={handleQuantityPlus} className="focus:outline-none rounded-r-full">
						<svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
							<path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
						</svg>
					</button>
				</div>

				<p className="text-xs font-light">{money.format(item.sale_price)} each</p>

				<p className="text-lg font-extrabold">{money.format(item.extended_sale_price)}</p>
			</div>
		</div>
	)
}

export default function Cart(): JSX.Element {
	const { contents } = useCart()
	const money = useMoneyFormatter()

	if (contents === undefined) {
		return (
			<div className="py-32 flex justify-center items-center">
				<LoadingIcon />
			</div>
		)
	}

	return (
		<>
			<Head>
				<title key="page-title">Cart | Pace International</title>
			</Head>

			{ contents === null || !Array.isArray(contents.line_items.physical_items) || contents.line_items.physical_items.length === 0 ? (
				<div className="text-center py-32 px-4 container mx-auto">
					<p className="text-2xl font-bold md:text-3xl">Your cart is empty.</p>
					{/* TODO: Add featured products */}
					{/* <p className="mt-2 md:text-lg md:mt-3">Check out what we’re featuring now!</p> */}

					<Link href="/">
						<a className="w-full inline-block py-1 text-xs bg-gradient-to-b from-green-400 via-green-500 to-green-500 text-white rounded-full uppercase font-extrabold shadow-md leading-5 mt-4 max-w-sm sm:text-base sm:py-2 md:mt-6 lg:text-xl">Back To Home</a>
					</Link>
				</div>
			) : (
				<div className="bg-gray-100 overflow-x-hidden">
					<div className="sm:py-5 sm:px-8 xl:pt-8 xl:pb-14 xl:flex xl:container xl:mx-auto">
						<div className="container mx-auto pt-4 px-4 sm:pt-0 sm:px-0 xl:max-w-full">
							<p className="hidden xl:block xl:text-4xl xl:font-extrabold">Your Cart</p>

							{contents.line_items.physical_items.map(item => (
								<LineItem key={item.sku} item={item} />
							))}
						</div>

						<div className="bg-white pt-3 pb-9 px-8 mt-4 h-96 sm:mt-5 sm:container sm:mx-auto sm:shadow sm:rounded-lg sm:max-w-md sm:pb-6 xl:mt-0 xl:ml-12 xl:shadow xl:max-w-sm 2xl:ml-16 2xl:max-w-md">
							<h3 className="text-center sm:text-xl sm:font-bold md:text-2xl 2xl:text-3xl">Cart Summary</h3>

							<div className="h-px bg-gray-200 rounded-full mt-6 sm:mt-4 xl:mt-5"></div>

							<div className="mt-6 text-sm space-y-1 md:text-base sm:mt-4">
								<div className="flex justify-between items-center">
									<p>Subtotal ({contents.line_items.physical_items.reduce<number>((acc, { quantity: cur }) => (acc + cur), 0)} items)</p>
									<p>{money.format(contents.base_amount)}</p>
								</div>
							</div>

							<div className="h-px bg-gray-200 rounded-full mt-6 sm:mt-4 xl:mt-5"></div>

							<div className="flex justify-between items-center mt-6 text-lg font-bold sm:mt-4 xl:mt-5">
								<p>Total</p>
								<p>{money.format(contents.base_amount)}</p>
							</div>

							<form action={`${process.env.NEXT_PUBLIC_BACKEND_API_URL!}/checkout`} method="POST">
								<button type="submit" className="w-full mt-6 py-3 text-lg bg-gradient-to-b from-green-400 via-green-500 to-green-500 text-white rounded-full uppercase font-extrabold shadow-md leading-5 sm:mt-4 sm:px-3.5 md:px-5 xl:mt-5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400">Checkout</button>
							</form>

							<Link href="/">
								<a className="hidden text-green-400 underline text-base text-center mt-5 xl:block">Continue Shopping</a>
							</Link>

							<p className="hidden text-sm mt-5 text-center xl:block">Shipping and taxes calculated at checkout.</p>
						</div>
					</div>
				</div>
			)}
		</>
	)
}

Cart.Layout = Layout
